import { ChangeDetectionStrategy, Component, inject, input, OnInit } from '@angular/core';
import { IonCardContent, ModalController } from '@ionic/angular';
import { User } from '../../entities/user';
import {
    AnnouncementAssignableUserRoles,
    BasicAssignableUserRole,
    ChatAssignableUserRoles,
    KnowledgeAssignableUserRoles,
    MeetingAssignableUserRoles,
    NotesAssignableUserRoles,
    UserRoles,
} from '../../entities/user-roles';
import { UsersService } from '../../services/user';
import { ToastService } from '../../../common/services/toast-service/toast-service.service';
import { IonicColor } from '../../../common/entities/toast/ionic-color';
import { ItemType, TableConfig, TableItem } from '../../../table/entities/table';
import { LoadingService } from '../../../common/services/loading/loading.service';
import { RoleListModalComponent } from '../role-list-modal/role-list-modal.component';
import { Logger, LoggingService } from '../../../logging/logging.service';
import { StringItemAdapterComponent } from '../../../table/components/table-adapter/string-item-adapter.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ICardActionButton } from '../../../common/entities/card-action-button.interface';
import { throwError } from 'rxjs';
import { FeatureConfigs } from '../../../common/entities/curafida-frontend-configuration';
import { PaginatedResponse } from '../../../common/entities/paginated-response';
import { CurafidaCardComponent } from '../../../common/components/curafida-card/curafida-card.component';
import { CurafidaTableComponent } from '../../../table/components/curafida-table/curafida-table.component';
import { ConfigService } from '../../../config/services/config.service';
import { LoadingProcess } from '../../../table/components/curafida-table/loading.process';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'user-role-card',
    templateUrl: './user-role-card.component.html',
    styleUrls: ['./user-role-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonCardContent, CurafidaCardComponent, CurafidaTableComponent],
})
export class UserRoleCardComponent implements OnInit {
    readonly isEditEnabled = input(false);
    readonly isNewUser = input<boolean>();
    readonly featureConfigs = input<FeatureConfigs>();
    readonly isOwnDetails = input<boolean>();
    readonly hasRightToModifyUser = input<boolean>();
    readonly loggedInUser = input<User>();
    readonly url = input<string>();
    readonly user = input<User>();
    readonly isMobile = input<boolean>();
    UserRoles = UserRoles;
    roleListConfig: TableConfig<{ role: string }[]> = new TableConfig<{ role: string }[]>();
    rolesActionButtons: ICardActionButton[] = [];
    protected modalController = inject(ModalController);
    protected usersService = inject(UsersService);
    protected readonly log: Logger;
    private toastService = inject(ToastService);
    private loadingService = inject(LoadingService);
    private loggingService = inject(LoggingService);
    private readonly configService = inject(ConfigService);
    loadingProcess = LoadingProcess.initLoadingProcess();

    constructor() {
        this.log = this.loggingService.getLogger(this.constructor.name);
        this.rolesActionButtons.push({
            icon: 'add',
            id: 'add-roles',
            isDisabled: false,
            isHidden: false,
            isIconButton: this.isMobile(),
            title: 'USER.ASSIGN_ROLES',
        });
    }

    async ngOnInit(): Promise<void> {
        if (this.isNewUser()) {
            this.user().roles = [this.usersService.readRoleFromUrl(this.url())];
        }
        await this.initRolesConfig();
    }

    initRoleList(): void {
        this.loadingProcess = LoadingProcess.initLoadingProcess();
        const user = this.user();
        if (user.roles) {
            this.roleListConfig.list.items = [];
            try {
                for (const role of user.roles) {
                    if (Object.keys(BasicAssignableUserRole).find((e) => role.toString() === e.toString())) {
                        this.addToRoleListConfig(role);
                    }
                    const featureConfigs = this.featureConfigs();
                    if (
                        this.configService.config()?.configuration?.features?.announcement?.enabled &&
                        Object.keys(AnnouncementAssignableUserRoles).find((e) => role.toString() === e.toString())
                    ) {
                        this.addToRoleListConfig(role);
                    }
                    if (
                        this.configService.config()?.configuration?.features?.chat?.enabled &&
                        Object.keys(ChatAssignableUserRoles).find((e) => role.toString() === e.toString())
                    ) {
                        this.addToRoleListConfig(role);
                    }
                    if (
                        this.configService.config()?.configuration?.features?.note?.enabled &&
                        Object.keys(NotesAssignableUserRoles).find((e) => role.toString() === e.toString())
                    ) {
                        this.addToRoleListConfig(role);
                    }
                    if (
                        featureConfigs?.knowledge?.enabled &&
                        Object.keys(KnowledgeAssignableUserRoles).find((e) => role.toString() === e.toString())
                    ) {
                        this.addToRoleListConfig(role);
                    }
                    if (
                        this.configService.config()?.configuration?.features?.course?.enabled &&
                        Object.keys(MeetingAssignableUserRoles).find((e) => role.toString() === e.toString())
                    ) {
                        this.addToRoleListConfig(role);
                    }
                }
                this.loadingProcess = LoadingProcess.finishedSuccessfullyLoadingProcess();
            } catch (err) {
                this.log.error('Error in initRoleList', err);
                this.loadingProcess = LoadingProcess.finishedWithErrorLoadingProcess();
            } finally {
                this.roleListConfig.list = PaginatedResponse.init(this.roleListConfig.list.items);
            }
        } else {
            this.roleListConfig.list = PaginatedResponse.init(this.roleListConfig.list.items);
            this.loadingProcess = LoadingProcess.finishedSuccessfullyLoadingProcess();
        }
    }

    async changeRole(): Promise<void> {
        const selectedRole: { name: string }[] = [];
        if (this.roleListConfig.list.items) {
            for (const role of this.roleListConfig.list.items) {
                const newRole = { name: role.role };
                selectedRole.push(newRole);
            }
        }
        const modal = await this.modalController.create({
            component: RoleListModalComponent,
            cssClass: 'full-width-modal',
            componentProps: {
                alreadyAssignedUserRoles: selectedRole,
            },
        });
        await modal.present();
        const { data } = await modal.onDidDismiss();
        if (data) {
            this.log.debug('data', data);
            try {
                this.changeUserRoles(data, this.roleListConfig.list.items, this.isNewUser());
            } catch (err) {
                this.log.error('Error in changeRole', err);
            } finally {
                this.roleListConfig.list = PaginatedResponse.init(data);
            }
        }
    }

    changeUserRoles(newRolesList: { role: string }[], previousRoleList: { role: string }[], isNewUser: boolean) {
        const rolesToAdd: BasicAssignableUserRole[] = [];
        const rolesToRemove: BasicAssignableUserRole[] = [];
        for (const previousRole of previousRoleList) {
            if (!newRolesList.find((i) => i.role === previousRole.role)) {
                if (!isNewUser) {
                    rolesToRemove.push(previousRole.role as BasicAssignableUserRole);
                }
            }
        }
        for (const newRole of newRolesList) {
            if (!previousRoleList.find((i) => i.role === newRole.role)) {
                if (!isNewUser) {
                    rolesToAdd.push(newRole.role as BasicAssignableUserRole);
                }
            }
        }
        if (!isNewUser) {
            this.loadingService.startLoadingModal();
            try {
                if (rolesToAdd.length > 0) {
                    this.usersService.assignRolesToUser$(this.user().username, { roleNames: rolesToAdd }).subscribe();
                }
                if (rolesToRemove.length > 0) {
                    this.usersService
                        .removeRolesFromUser$(this.user().username, { roleNames: rolesToRemove })
                        .subscribe();
                }
            } catch (error) {
                this.log.error('Error in showDeletePrompt', error);
                this.toastService.showToast('USER.ROLLE.UPDATE_ERROR', IonicColor.danger);
                return throwError(() => error);
            } finally {
                this.toastService.showToast(ToastService.changeSavedMessage, IonicColor.success);
                this.loadingService.stopLoadingModal();
            }
        }
    }

    private addToRoleListConfig(role: string) {
        this.roleListConfig.list.items.push({ role: role });
        this.roleListConfig.list = PaginatedResponse.init(this.roleListConfig.list.items);
    }

    private async initRolesConfig() {
        this.roleListConfig.emptyListLabel = 'USER.ROLLE.ANY_ASSIGN';
        this.roleListConfig.itemSettings = [
            TableItem.create({
                id: 'role',
                prop: 'role',
                header: 'Name',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '40%',
            }),
        ];
        this.initRoleList();
    }
}
